import { useState } from "react";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Slider from "@mui/material/Slider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Tooltip from "@mui/material/Tooltip";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Tool, CMMIRating, ToolStatus, AgenticScore } from "../../types";
import { useAppStore } from "../../store";
import {
  getRatingColor,
  CMMI_LEVELS,
  AGENTIC_DIMENSIONS,
  calculateAgenticTotal,
} from "../../constants/ratings";
import { DragHandle } from "../shared/DragHandle";

interface ToolItemProps {
  stackId: string;
  funcId: string;
  tool: Tool;
}

export function ToolItem({ stackId, funcId, tool }: ToolItemProps) {
  const { updateTool, deleteTool } = useAppStore();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `tool:${stackId}:${funcId}:${tool.id}`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: tool.name,
    currentRating: tool.currentRating,
    status: tool.status,
    notes: tool.notes || "",
    proposedTool: tool.proposedTool || "",
    proposedRating: tool.proposedRating,
    agenticScore: tool.agenticScore || {
      automatability: 0,
      impact: 0,
      frequency: 0,
      dataReadiness: 0,
      riskProfile: 0,
    },
  });

  const handleOpen = () => {
    setFormData({
      name: tool.name,
      currentRating: tool.currentRating,
      status: tool.status,
      notes: tool.notes || "",
      proposedTool: tool.proposedTool || "",
      proposedRating: tool.proposedRating,
      agenticScore: tool.agenticScore || {
        automatability: 0,
        impact: 0,
        frequency: 0,
        dataReadiness: 0,
        riskProfile: 0,
      },
    });
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleSave = () => {
    const hasAgenticScore = Object.values(formData.agenticScore).some(
      (v) => v > 0,
    );
    updateTool(stackId, funcId, tool.id, {
      name: formData.name.trim(),
      currentRating: formData.currentRating,
      status: formData.status,
      notes: formData.notes.trim() || undefined,
      proposedTool: formData.proposedTool.trim() || undefined,
      proposedRating: formData.proposedRating,
      agenticScore: hasAgenticScore ? formData.agenticScore : undefined,
    });
    setIsDialogOpen(false);
  };

  const handleAgenticScoreChange = (
    dimension: keyof AgenticScore,
    value: number,
  ) => {
    setFormData({
      ...formData,
      agenticScore: {
        ...formData.agenticScore,
        [dimension]: value,
      },
    });
  };

  const agenticTotal = calculateAgenticTotal(
    formData.agenticScore as unknown as Record<string, number>,
  );

  const handleDelete = () => {
    if (confirm(`Delete tool "${tool.name}"?`)) {
      deleteTool(stackId, funcId, tool.id);
      setIsDialogOpen(false);
    }
  };

  const ratingColor = getRatingColor(tool.currentRating);

  return (
    <>
      <ListItem
        ref={setNodeRef}
        style={style}
        component="div"
        sx={{
          borderLeft: 4,
          borderColor: ratingColor,
          cursor: "pointer",
          "&:hover": {
            bgcolor: "action.hover",
          },
          py: 0.5,
          display: "flex",
          alignItems: "center",
        }}
      >
        <DragHandle
          listeners={listeners}
          attributes={attributes}
          size="small"
        />
        {tool.status === "Proposed" && (
          <ListItemIcon sx={{ minWidth: 28 }}>
            <HelpOutlineIcon fontSize="small" color="info" />
          </ListItemIcon>
        )}
        <ListItemText
          onClick={handleOpen}
          primary={tool.name}
          primaryTypographyProps={{
            variant: "body2",
            sx: {
              textDecoration:
                tool.status === "Retired" ? "line-through" : "none",
              color:
                tool.status === "Retired" ? "text.disabled" : "text.primary",
            },
          }}
        />
      </ListItem>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tool</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Tool Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              fullWidth
              autoFocus
            />

            <FormControl fullWidth>
              <InputLabel>CMMI Rating</InputLabel>
              <Select
                value={formData.currentRating}
                label="CMMI Rating"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    currentRating: e.target.value as CMMIRating,
                  })
                }
              >
                {([0, 1, 2, 3, 4] as CMMIRating[]).map((rating) => (
                  <MenuItem key={rating} value={rating}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: 1,
                          bgcolor: CMMI_LEVELS[rating].color,
                        }}
                      />
                      {rating} - {CMMI_LEVELS[rating].label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as ToolStatus,
                  })
                }
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Proposed">Proposed</MenuItem>
                <MenuItem value="Retired">Retired</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              fullWidth
              multiline
              rows={2}
            />

            {/* Proposed Tool Section */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: 1, borderColor: "divider" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Proposed Replacement
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Proposed Tool"
                    value={formData.proposedTool}
                    onChange={(e) =>
                      setFormData({ ...formData, proposedTool: e.target.value })
                    }
                    fullWidth
                    size="small"
                    placeholder="Name of replacement tool"
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Target Rating</InputLabel>
                    <Select
                      value={formData.proposedRating ?? ""}
                      label="Target Rating"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          proposedRating:
                            e.target.value === ""
                              ? undefined
                              : (e.target.value as CMMIRating),
                        })
                      }
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {([0, 1, 2, 3, 4] as CMMIRating[]).map((rating) => (
                        <MenuItem key={rating} value={rating}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: 0.5,
                                bgcolor: CMMI_LEVELS[rating].color,
                              }}
                            />
                            {rating} - {CMMI_LEVELS[rating].label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* Agentic Score Section */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{ border: 1, borderColor: "divider" }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  <SmartToyIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2">
                    Agentic Potential Score
                  </Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Tooltip title="Total score out of 20">
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{ fontWeight: "bold", mr: 1 }}
                    >
                      {agenticTotal}/20
                    </Typography>
                  </Tooltip>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {AGENTIC_DIMENSIONS.map((dim) => (
                    <Box key={dim.key}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 0.5,
                        }}
                      >
                        <Tooltip title={dim.description} placement="left">
                          <Typography variant="body2">{dim.label}</Typography>
                        </Tooltip>
                        <Typography variant="body2" color="text.secondary">
                          {formData.agenticScore[dim.key]}
                        </Typography>
                      </Box>
                      <Slider
                        value={formData.agenticScore[dim.key]}
                        onChange={(_, value) =>
                          handleAgenticScoreChange(dim.key, value as number)
                        }
                        min={0}
                        max={4}
                        step={1}
                        marks
                        size="small"
                      />
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <IconButton color="error" onClick={handleDelete}>
            <DeleteIcon />
          </IconButton>
          <Box>
            <Button onClick={handleClose} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.name.trim()}
            >
              Save
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}
